import { Finding, FindingParameters, StepTask } from '@telespot/domain';
import { FindingChange, FindingItem } from '../../entities';

/**
 * Errors that can be thrown by the {@link FindingService}
 */
export enum FindingError {
  FINDING_NOT_FOUND = 'finding.finding_not_found',
  INVALID_CHANGE_TYPE = 'finding.invalid_change_type',
  CHANGE_APPLICATION_FAILED = 'finding.change_application_failed',
}

/**
 * Service for managing finding operations and change applications.
 */
export class FindingService {
  /**
   * Applies a finding change to a finding item.
   * @param finding The finding to apply the change to
   * @param change The change to apply
   * @returns The updated finding with the change applied
   */
  public static applyChange(finding: FindingItem, change: FindingChange): Finding {
    if (!finding) {
      throw new Error(FindingError.FINDING_NOT_FOUND);
    }

    if (!change) {
      throw new Error(FindingError.INVALID_CHANGE_TYPE);
    }

    try {
      const findingParams: FindingParameters = {
        id: finding.id,
        uuid: finding.uuid,
        version: finding.version,
        type: finding.type,
        analysisId: finding.analysisId,
        assetId: finding.assetId,
        creatorId: finding.creatorId,
        creatorEntity: finding.creatorEntity,
        pipelineStepId: finding.pipelineStepId,
        pipelineId: finding.pipelineId,
        originId: finding.originId,
        createdAt: finding.createdAt,
        updatedAt: new Date(),
        deletedAt: finding.deletedAt,
        data: this.createFindingDataFromChange(change),
      };

      return new Finding(findingParams);
    } catch (error) {
      throw new Error(FindingError.CHANGE_APPLICATION_FAILED);
    }
  }

  /**
   * Creates finding data from a change object.
   * @param change The change containing the new data
   * @returns The formatted finding data
   */
  private static createFindingDataFromChange(change: FindingChange): any {
    if (change.type === 'text') {
      return {
        content: [{ value: change.currentData as string }],
      };
    } else if (change.type === 'classification') {
      const options: { [key: string]: number } = {};
      (change.currentData as string[]).forEach((option) => {
        options[option] = 1.0;
      });
      return {
        content: [{ options }],
      };
    }
    return { content: [] };
  }

  /**
   * Extracts unique finding IDs from a map of changes.
   * @param changes Map of finding changes
   * @returns Array of unique finding IDs
   */
  public static extractUniqueFindingIds(changes: Map<string, FindingChange>): string[] {
    return [...new Set(Array.from(changes.values()).map((change) => change.id))];
  }

  /**
   * Extracts finding value from data based on finding type
   */
  public static extractFindingValue(data: any, type: StepTask): string | string[] | null {
    if (!data) {
      return null;
    }

    switch (type) {
      case StepTask.TEXT:
        return this.extractTextValue(data);
      case StepTask.CLASSIFICATION:
        return this.extractClassificationValue(data);
      default:
        return null;
    }
  }

  private static extractTextValue(data: any): string | null {
    if (data?.content && Array.isArray(data.content) && data.content.length > 0) {
      return data.content[0]?.value || null;
    }
    return null;
  }

  private static extractClassificationValue(data: any): string[] | null {
    if (data?.content && Array.isArray(data.content) && data.content.length > 0) {
      const options = data.content[0]?.options;
      if (options && typeof options === 'object') {
        return Object.keys(options).filter((key) => options[key] > 0);
      }
    }
    return null;
  }
}
